import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogTitleProps,
  Paper,
  Stack,
  styled,
} from '@mui/material';
import classNames from 'classnames';

import { Button, ErrorMessage } from '@components/base';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { getNewFilterGroup } from '@utils/filterUtils';

import useFilterDialog from './hooks/useFilterDialog';
import { useFilterPaperElevation } from './hooks/useFilterPaperElevation';
import FilterGroup from './FilterGroup';
import FilterList from './FilterList';
import WithCombinatorMenu from './WithCombinatorMenu';

const StyledDialogContent = styled(DialogContent)`
  max-width: 100%;
  overflow-x: auto;
  padding: ${({ theme }) => theme.spacing(0, 1.5, 0.5)};

  & > div {
    min-width: 800px;
  }

  &.dense {
    padding: 0;
  }
`;

type FilterDialogContentProps = {
  dense?: boolean;
  heading?: string;
  HeadingProps?: DialogTitleProps;
};

const FilterDialogContent: FC<FilterDialogContentProps> = ({
  dense,
  HeadingProps: headingProps,
  heading,
}) => {
  const { t } = useTranslation();

  const singleGroupElevation = useFilterPaperElevation(0);

  const {
    filterDefLoad: { isLoading: filterDefsLoading, data: filterDefs, error: filterDefsError },
    isReadonly,
  } = useSearchAndFilter();

  const {
    handleCombinatorChange,
    handleRemoveFilter,
    hasRootLevelGroups,
    nestedFilterListChangeHandler,
    searchFilter,
    setSearchFilter,
  } = useFilterDialog();

  return filterDefsError ? (
    <Fragment>
      <DialogTitle {...headingProps}>{t('searchAndFilter.searchFilters')}</DialogTitle>

      <DialogContent>
        <ErrorMessage error={filterDefsError} />
      </DialogContent>
    </Fragment>
  ) : (
    <Fragment>
      <Stack
        alignItems='flex-start'
        direction='row'
        justifyContent='space-between'
        spacing={1}
        width={1}
      >
        <DialogTitle {...headingProps}>{heading ?? t('searchAndFilter.searchFilters')}</DialogTitle>

        {!isReadonly && !filterDefsError && (
          <DialogActions>
            <Button onClick={() => setSearchFilter(getNewFilterGroup(true))}>{t('reset')}</Button>
          </DialogActions>
        )}
      </Stack>

      <StyledDialogContent className={classNames({ dense })}>
        {filterDefsLoading ? (
          <Box alignItems='center' display='flex' justifyContent='center' p={4} width={200}>
            <CircularProgress />
          </Box>
        ) : (
          filterDefs &&
          (hasRootLevelGroups ? (
            <WithCombinatorMenu
              disabled={isReadonly}
              onChange={handleCombinatorChange}
              value={searchFilter.logicalOperator}
            >
              <Stack flex={1} spacing={1}>
                {searchFilter.fields.map((filterField, index) =>
                  filterField.filter ? (
                    <FilterGroup
                      filterGroup={filterField.filter}
                      index={index}
                      key={filterField.id}
                      nestingLevel={0}
                      removeFilterGroup={handleRemoveFilter(filterField.id)}
                      updateFilterGroup={nestedFilterListChangeHandler(filterField.id)}
                    />
                  ) : null
                )}
              </Stack>
            </WithCombinatorMenu>
          ) : (
            <Paper
              elevation={singleGroupElevation}
              sx={{
                p: 1,
                bgcolor: theme =>
                  theme.palette.mode === 'light' ? theme.palette.background.default : undefined,
              }}
              variant='elevation'
            >
              <FilterList
                filterDefs={filterDefs}
                filterGroup={searchFilter}
                nestingLevel={1}
                onChange={setSearchFilter}
              />
            </Paper>
          ))
        )}
      </StyledDialogContent>
    </Fragment>
  );
};

export default FilterDialogContent;
